import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CATEGORY_LABELS, StatusBadge, Tabs } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, Checkbox, Input, Select, Switch, Textarea } from '@/components/ui/primitives';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { SlaTab } from './sla-tab';
import type { AreaConfig, CategoryConfig, PortalAccess, RoleItem, StatusCategory, StatusConfig, TeamConfig, UserItem, Paginated } from '@/lib/types';

const ACCESS: { value: PortalAccess; label: string; hint: string }[] = [
  { value: 'PUBLIC', label: 'Öffentlich', hint: 'Alle Kunden; anonyme Anfragen über öffentliche Formulare (ab Phase 4)' },
  { value: 'REGISTERED', label: 'Registrierte Kunden', hint: 'Jeder Kunde mit Portalzugang' },
  { value: 'CONTRACT', label: 'Wartungsvertragskunden', hint: 'Kunden mit aktivem Vertrag (Verträge ab Phase 7) oder individueller Freischaltung' },
  { value: 'INDIVIDUAL', label: 'Individuell freigeschaltet', hint: 'Nur in der Kundenakte freigeschaltete Kunden' },
  { value: 'INTERNAL', label: 'Nur intern', hint: 'Im Portal unsichtbar, auch für zugeordnete Kunden' },
  { value: 'DISABLED', label: 'Deaktiviert', hint: 'Keine neuen Portalanfragen; bestehende bleiben sichtbar' },
];

const onError = (e: unknown) => toast.error(errorMessage(e));

function ColorInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex gap-2">
      <input type="color" className="h-9 w-12 cursor-pointer rounded-md border bg-card p-1" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Farbe wählen" />
      <Input className="font-mono" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// --- Bereiche -----------------------------------------------------------------
type AreaForm = Omit<AreaConfig, 'id' | 'key' | 'ticketCount'>;
const newArea: AreaForm = {
  name: '',
  description: '',
  color: '#64748b',
  portalAccess: 'REGISTERED',
  portalShowStatus: true,
  portalShowHistory: true,
  portalShowAssignee: false,
  portalShowDueDate: false,
  sortOrder: 100,
  active: true,
  roleIds: [],
};

function AreasTab() {
  const qc = useQueryClient();
  const key = ['ticket-config', 'areas'];
  const { data } = useQuery({ queryKey: key, queryFn: () => api<AreaConfig[]>('/ticket-config/areas') });
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: () => api<RoleItem[]>('/roles') });
  const [edit, setEdit] = useState<{ id: string | null; form: AreaForm } | null>(null);
  const [confirmDialog, confirm] = useConfirm();
  const done = (list: AreaConfig[]) => {
    qc.setQueryData(key, list);
    qc.invalidateQueries({ queryKey: ['tickets', 'meta'] });
    setEdit(null);
  };
  const save = useMutation({
    mutationFn: () =>
      edit!.id
        ? api<AreaConfig[]>(`/ticket-config/areas/${edit!.id}`, { method: 'PUT', body: edit!.form })
        : api<AreaConfig[]>('/ticket-config/areas', { method: 'POST', body: edit!.form }),
    onSuccess: done,
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api<AreaConfig[]>(`/ticket-config/areas/${id}`, { method: 'DELETE' }), onSuccess: done, onError });
  const set = <K extends keyof AreaForm>(k: K, v: AreaForm[K]) => setEdit((e) => e && { ...e, form: { ...e.form, [k]: v } });
  const internalRoles = roles?.filter((r) => r.audience === 'INTERNAL') ?? [];

  return (
    <Card>
      {confirmDialog}
      <div className="flex justify-end border-b p-3">
        <Button size="sm" onClick={() => setEdit({ id: null, form: newArea })}>
          <Plus /> Bereich
        </Button>
      </div>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Bereich</TH>
            <TH>Kundenportal</TH>
            <TH className="hidden md:table-cell">Intern sichtbar für</TH>
            <TH className="w-24" />
          </TR>
        </THead>
        <TBody>
          {data?.map((a) => (
            <TR key={a.id}>
              <TD>
                <p className="flex items-center gap-2 font-medium">
                  <span className="size-2.5 rounded-full" style={{ background: a.color }} />
                  {a.name}
                  {!a.active && <Badge variant="outline">inaktiv</Badge>}
                </p>
                {a.description && <p className="text-xs text-muted-foreground">{a.description}</p>}
              </TD>
              <TD className="text-sm">{ACCESS.find((x) => x.value === a.portalAccess)?.label}</TD>
              <TD className="hidden text-sm md:table-cell">
                {a.roleIds.length === 0 ? 'alle mit Ticketrechten' : a.roleIds.map((r) => roles?.find((x) => x.id === r)?.name).filter(Boolean).join(', ')}
              </TD>
              <TD className="text-right">
                <Button variant="ghost" size="icon" className="size-8" onClick={() => setEdit({ id: a.id, form: { ...a, description: a.description ?? '' } })} aria-label={`${a.name} bearbeiten`}>
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:text-destructive"
                  disabled={a.ticketCount > 0}
                  title={a.ticketCount > 0 ? 'Enthält Tickets – stattdessen deaktivieren' : undefined}
                  onClick={async () => (await confirm({ title: `Bereich „${a.name}“ löschen?`, confirmLabel: 'Löschen', destructive: true })) && del.mutate(a.id)}
                  aria-label={`${a.name} löschen`}
                >
                  <Trash2 />
                </Button>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      <Dialog open={edit !== null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent wide>
          {edit && (
            <>
              <DialogHeader>
                <DialogTitle>{edit.id ? 'Bereich bearbeiten' : 'Neuer Bereich'}</DialogTitle>
              </DialogHeader>
              <DialogBody className="grid gap-5">
                <div className="grid gap-4 sm:grid-cols-[1fr_12rem_6rem]">
                  <Field label="Name" htmlFor="a-name">
                    <Input id="a-name" value={edit.form.name} onChange={(e) => set('name', e.target.value)} />
                  </Field>
                  <Field label="Farbe">
                    <ColorInput value={edit.form.color} onChange={(v) => set('color', v)} />
                  </Field>
                  <Field label="Reihenfolge" htmlFor="a-sort">
                    <Input id="a-sort" type="number" value={edit.form.sortOrder} onChange={(e) => set('sortOrder', Number(e.target.value))} />
                  </Field>
                </div>
                <Field label="Beschreibung (auch im Portal sichtbar)" htmlFor="a-desc">
                  <Textarea id="a-desc" rows={2} value={edit.form.description ?? ''} onChange={(e) => set('description', e.target.value)} />
                </Field>
                <fieldset className="grid gap-2">
                  <legend className="mb-1 text-sm font-medium">Wer darf im Kundenportal Anfragen stellen?</legend>
                  {ACCESS.map((o) => (
                    <label key={o.value} className="flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                      <input type="radio" name="access" className="mt-1 accent-[var(--primary)]" checked={edit.form.portalAccess === o.value} onChange={() => set('portalAccess', o.value)} />
                      <span>
                        <span className="block text-sm font-medium">{o.label}</span>
                        <span className="block text-xs text-muted-foreground">{o.hint}</span>
                      </span>
                    </label>
                  ))}
                </fieldset>
                <fieldset className="grid gap-3 sm:grid-cols-2">
                  <legend className="mb-1 text-sm font-medium">Kunden sehen im Portal …</legend>
                  {(
                    [
                      ['portalShowStatus', 'den (vereinfachten) Status'],
                      ['portalShowHistory', 'den Bearbeitungsverlauf'],
                      ['portalShowAssignee', 'den zuständigen Techniker'],
                      ['portalShowDueDate', 'den voraussichtlichen Termin'],
                    ] as const
                  ).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2.5 text-sm">
                      <Switch checked={edit.form[k]} onCheckedChange={(v) => set(k, v)} />
                      {label}
                    </label>
                  ))}
                </fieldset>
                <fieldset>
                  <legend className="mb-1 text-sm font-medium">Interne Sichtbarkeit</legend>
                  <p className="mb-2 text-xs text-muted-foreground">Ohne Auswahl sehen alle Mitarbeiter mit Ticketrechten diesen Bereich.</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {internalRoles.map((r) => (
                      <label key={r.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={edit.form.roleIds.includes(r.id)}
                          onCheckedChange={(v) => set('roleIds', v ? [...edit.form.roleIds, r.id] : edit.form.roleIds.filter((x) => x !== r.id))}
                        />
                        {r.name}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label className="flex items-center gap-2.5 text-sm">
                  <Switch checked={edit.form.active} onCheckedChange={(v) => set('active', v)} /> Bereich aktiv
                </label>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEdit(null)}>
                  Abbrechen
                </Button>
                <Button onClick={() => save.mutate()} loading={save.isPending} disabled={edit.form.name.trim().length < 2}>
                  Speichern
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// --- Status -------------------------------------------------------------------
type StatusForm = Omit<StatusConfig, 'id' | 'ticketCount'>;

function StatusesTab() {
  const qc = useQueryClient();
  const key = ['ticket-config', 'statuses'];
  const { data } = useQuery({ queryKey: key, queryFn: () => api<StatusConfig[]>('/ticket-config/statuses') });
  const [edit, setEdit] = useState<{ id: string | null; form: StatusForm } | null>(null);
  const [confirmDialog, confirm] = useConfirm();
  const done = (list: StatusConfig[]) => {
    qc.setQueryData(key, list);
    qc.invalidateQueries({ queryKey: ['tickets'] });
    setEdit(null);
  };
  const save = useMutation({
    mutationFn: () =>
      edit!.id
        ? api<StatusConfig[]>(`/ticket-config/statuses/${edit!.id}`, { method: 'PUT', body: edit!.form })
        : api<StatusConfig[]>('/ticket-config/statuses', { method: 'POST', body: edit!.form }),
    onSuccess: done,
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api<StatusConfig[]>(`/ticket-config/statuses/${id}`, { method: 'DELETE' }), onSuccess: done, onError });
  const set = <K extends keyof StatusForm>(k: K, v: StatusForm[K]) => setEdit((e) => e && { ...e, form: { ...e.form, [k]: v } });
  const name = (id: string) => data?.find((s) => s.id === id)?.name;

  return (
    <Card>
      {confirmDialog}
      <div className="flex items-center justify-between gap-2 border-b p-3">
        <p className="text-xs text-muted-foreground">Ohne festgelegte Folgestatus sind alle Wechsel erlaubt.</p>
        <Button
          size="sm"
          onClick={() =>
            setEdit({ id: null, form: { name: '', customerLabel: '', category: 'OPEN', color: '#64748b', sortOrder: 100, isDefault: false, active: true, nextStatusIds: [] } })
          }
        >
          <Plus /> Status
        </Button>
      </div>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Status</TH>
            <TH className="hidden sm:table-cell">Im Kundenportal</TH>
            <TH className="hidden md:table-cell">Kategorie</TH>
            <TH className="hidden lg:table-cell">Erlaubte Folgestatus</TH>
            <TH className="w-24" />
          </TR>
        </THead>
        <TBody>
          {data?.map((s) => (
            <TR key={s.id}>
              <TD>
                <div className="flex items-center gap-2">
                  <StatusBadge name={s.name} color={s.color} />
                  {s.isDefault && <Star className="size-3.5 fill-amber-400 text-amber-400" aria-label="Standard für neue Tickets" />}
                  {!s.active && <Badge variant="outline">inaktiv</Badge>}
                </div>
              </TD>
              <TD className="hidden sm:table-cell">{s.customerLabel}</TD>
              <TD className="hidden md:table-cell">{CATEGORY_LABELS[s.category]}</TD>
              <TD className="hidden text-xs text-muted-foreground lg:table-cell">{s.nextStatusIds.length ? s.nextStatusIds.map(name).join(', ') : 'alle'}</TD>
              <TD className="text-right">
                <Button variant="ghost" size="icon" className="size-8" onClick={() => setEdit({ id: s.id, form: s })} aria-label={`${s.name} bearbeiten`}>
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:text-destructive"
                  disabled={s.ticketCount > 0 || s.isDefault}
                  onClick={async () => (await confirm({ title: `Status „${s.name}“ löschen?`, confirmLabel: 'Löschen', destructive: true })) && del.mutate(s.id)}
                  aria-label={`${s.name} löschen`}
                >
                  <Trash2 />
                </Button>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <Dialog open={edit !== null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent wide>
          {edit && (
            <>
              <DialogHeader>
                <DialogTitle>{edit.id ? 'Status bearbeiten' : 'Neuer Status'}</DialogTitle>
                <DialogDescription>Interne Status können auf vereinfachte Kundenstatus abgebildet werden.</DialogDescription>
              </DialogHeader>
              <DialogBody className="grid gap-4 sm:grid-cols-2">
                <Field label="Interner Name" htmlFor="s-name">
                  <Input id="s-name" value={edit.form.name} onChange={(e) => set('name', e.target.value)} />
                </Field>
                <Field label="Anzeige im Kundenportal" htmlFor="s-label">
                  <Input id="s-label" value={edit.form.customerLabel} onChange={(e) => set('customerLabel', e.target.value)} />
                </Field>
                <Field label="Kategorie" htmlFor="s-cat" hint="Steuert Filter, Zeitstempel und Wiedereröffnung bei Kundenantwort">
                  <Select id="s-cat" value={edit.form.category} onChange={(e) => set('category', e.target.value as StatusCategory)}>
                    {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </Select>
                </Field>
                <div className="grid grid-cols-[1fr_6rem] gap-3">
                  <Field label="Farbe">
                    <ColorInput value={edit.form.color} onChange={(v) => set('color', v)} />
                  </Field>
                  <Field label="Reihenfolge" htmlFor="s-sort">
                    <Input id="s-sort" type="number" value={edit.form.sortOrder} onChange={(e) => set('sortOrder', Number(e.target.value))} />
                  </Field>
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className="flex items-center gap-2.5 text-sm">
                    <Switch checked={edit.form.isDefault} onCheckedChange={(v) => set('isDefault', v)} /> Standardstatus für neue Tickets
                  </label>
                  <label className="flex items-center gap-2.5 text-sm">
                    <Switch checked={edit.form.active} onCheckedChange={(v) => set('active', v)} /> aktiv
                  </label>
                </div>
                <fieldset className="sm:col-span-2">
                  <legend className="mb-2 text-sm font-medium">Erlaubte Folgestatus (Workflow)</legend>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {data
                      ?.filter((s) => s.id !== edit.id)
                      .map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={edit.form.nextStatusIds.includes(s.id)}
                            onCheckedChange={(v) => set('nextStatusIds', v ? [...edit.form.nextStatusIds, s.id] : edit.form.nextStatusIds.filter((x) => x !== s.id))}
                          />
                          {s.name}
                        </label>
                      ))}
                  </div>
                </fieldset>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEdit(null)}>
                  Abbrechen
                </Button>
                <Button onClick={() => save.mutate()} loading={save.isPending} disabled={edit.form.name.trim().length < 2 || edit.form.customerLabel.trim().length < 2}>
                  Speichern
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// --- Kategorien -----------------------------------------------------------------
function CategoriesTab() {
  const qc = useQueryClient();
  const key = ['ticket-config', 'categories'];
  const { data } = useQuery({ queryKey: key, queryFn: () => api<CategoryConfig[]>('/ticket-config/categories') });
  const { data: areas } = useQuery({ queryKey: ['ticket-config', 'areas'], queryFn: () => api<AreaConfig[]>('/ticket-config/areas') });
  const [edit, setEdit] = useState<{ id: string | null; form: Omit<CategoryConfig, 'id'> } | null>(null);
  const done = (list: CategoryConfig[]) => {
    qc.setQueryData(key, list);
    qc.invalidateQueries({ queryKey: ['tickets', 'meta'] });
    setEdit(null);
  };
  const save = useMutation({
    mutationFn: () =>
      edit!.id
        ? api<CategoryConfig[]>(`/ticket-config/categories/${edit!.id}`, { method: 'PUT', body: edit!.form })
        : api<CategoryConfig[]>('/ticket-config/categories', { method: 'POST', body: edit!.form }),
    onSuccess: done,
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api<CategoryConfig[]>(`/ticket-config/categories/${id}`, { method: 'DELETE' }), onSuccess: done, onError });
  const roots = data?.filter((c) => !c.parentId) ?? [];
  const areaName = (id: string | null) => (id ? areas?.find((a) => a.id === id)?.name : 'alle Bereiche');

  return (
    <Card>
      <div className="flex justify-end border-b p-3">
        <Button size="sm" onClick={() => setEdit({ id: null, form: { name: '', areaId: null, parentId: null, sortOrder: 100, active: true } })}>
          <Plus /> Kategorie
        </Button>
      </div>
      <Table>
        <TBody>
          {roots.length === 0 && <EmptyRow colSpan={3}>Noch keine Kategorien.</EmptyRow>}
          {roots.flatMap((c) => [c, ...(data?.filter((s) => s.parentId === c.id) ?? [])]).map((c) => (
            <TR key={c.id}>
              <TD className={c.parentId ? 'pl-10' : 'font-medium'}>
                {c.parentId && '↳ '}
                {c.name} {!c.active && <Badge variant="outline">inaktiv</Badge>}
              </TD>
              <TD className="text-sm text-muted-foreground">{areaName(c.areaId)}</TD>
              <TD className="w-24 text-right">
                <Button variant="ghost" size="icon" className="size-8" onClick={() => setEdit({ id: c.id, form: c })} aria-label={`${c.name} bearbeiten`}>
                  <Pencil />
                </Button>
                <Button variant="ghost" size="icon" className="size-8 text-destructive hover:text-destructive" onClick={() => del.mutate(c.id)} aria-label={`${c.name} löschen`}>
                  <Trash2 />
                </Button>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <Dialog open={edit !== null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          {edit && (
            <>
              <DialogHeader>
                <DialogTitle>{edit.id ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</DialogTitle>
              </DialogHeader>
              <DialogBody className="grid gap-4">
                <Field label="Name" htmlFor="k-name">
                  <Input id="k-name" value={edit.form.name} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, name: e.target.value } })} />
                </Field>
                <Field label="Bereich" htmlFor="k-area">
                  <Select id="k-area" value={edit.form.areaId ?? ''} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, areaId: e.target.value || null, parentId: null } })}>
                    <option value="">Alle Bereiche</option>
                    {areas?.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Oberkategorie" htmlFor="k-parent">
                  <Select id="k-parent" value={edit.form.parentId ?? ''} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, parentId: e.target.value || null } })}>
                    <option value="">– keine (Hauptkategorie) –</option>
                    {roots
                      .filter((r) => r.id !== edit.id && r.areaId === edit.form.areaId)
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                  </Select>
                </Field>
                <label className="flex items-center gap-2.5 text-sm">
                  <Switch checked={edit.form.active} onCheckedChange={(v) => setEdit({ ...edit, form: { ...edit.form, active: v } })} /> aktiv
                </label>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEdit(null)}>
                  Abbrechen
                </Button>
                <Button onClick={() => save.mutate()} loading={save.isPending} disabled={edit.form.name.trim().length < 2}>
                  Speichern
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// --- Teams --------------------------------------------------------------------
function TeamsTab() {
  const qc = useQueryClient();
  const key = ['ticket-config', 'teams'];
  const { data } = useQuery({ queryKey: key, queryFn: () => api<TeamConfig[]>('/ticket-config/teams') });
  const { data: staff } = useQuery({ queryKey: ['users', 'staff'], queryFn: () => api<Paginated<UserItem>>('/users?pageSize=100&status=ACTIVE&kind=staff') });
  const [edit, setEdit] = useState<{ id: string | null; name: string; description: string; memberIds: string[] } | null>(null);
  const done = (list: TeamConfig[]) => {
    qc.setQueryData(key, list);
    qc.invalidateQueries({ queryKey: ['tickets', 'meta'] });
    setEdit(null);
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { name: edit!.name, description: edit!.description || null, memberIds: edit!.memberIds };
      return edit!.id ? api<TeamConfig[]>(`/ticket-config/teams/${edit!.id}`, { method: 'PUT', body }) : api<TeamConfig[]>('/ticket-config/teams', { method: 'POST', body });
    },
    onSuccess: done,
    onError,
  });
  const del = useMutation({ mutationFn: (id: string) => api<TeamConfig[]>(`/ticket-config/teams/${id}`, { method: 'DELETE' }), onSuccess: done, onError });

  return (
    <Card>
      <div className="flex justify-end border-b p-3">
        <Button size="sm" onClick={() => setEdit({ id: null, name: '', description: '', memberIds: [] })}>
          <Plus /> Team
        </Button>
      </div>
      <Table>
        <TBody>
          {data?.length === 0 && <EmptyRow colSpan={3}>Noch keine Teams.</EmptyRow>}
          {data?.map((t) => (
            <TR key={t.id}>
              <TD>
                <p className="font-medium">{t.name}</p>
                {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
              </TD>
              <TD className="text-sm text-muted-foreground">{t.members.map((m) => m.name).join(', ') || 'keine Mitglieder'}</TD>
              <TD className="w-24 text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => setEdit({ id: t.id, name: t.name, description: t.description ?? '', memberIds: t.members.map((m) => m.id) })}
                  aria-label={`${t.name} bearbeiten`}
                >
                  <Pencil />
                </Button>
                <Button variant="ghost" size="icon" className="size-8 text-destructive hover:text-destructive" onClick={() => del.mutate(t.id)} aria-label={`${t.name} löschen`}>
                  <Trash2 />
                </Button>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <Dialog open={edit !== null} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          {edit && (
            <>
              <DialogHeader>
                <DialogTitle>{edit.id ? 'Team bearbeiten' : 'Neues Team'}</DialogTitle>
              </DialogHeader>
              <DialogBody className="grid gap-4">
                <Field label="Name" htmlFor="t-name">
                  <Input id="t-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                </Field>
                <Field label="Beschreibung" htmlFor="t-desc">
                  <Input id="t-desc" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
                </Field>
                <fieldset>
                  <legend className="mb-2 text-sm font-medium">Mitglieder</legend>
                  <div className="grid max-h-64 gap-2 overflow-y-auto">
                    {staff?.items.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={edit.memberIds.includes(u.id)}
                          onCheckedChange={(v) => setEdit({ ...edit, memberIds: v ? [...edit.memberIds, u.id] : edit.memberIds.filter((x) => x !== u.id) })}
                        />
                        {u.firstName} {u.lastName}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEdit(null)}>
                  Abbrechen
                </Button>
                <Button onClick={() => save.mutate()} loading={save.isPending} disabled={edit.name.trim().length < 2}>
                  Speichern
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function TicketConfigPage() {
  const [tab, setTab] = useState<'areas' | 'statuses' | 'categories' | 'teams' | 'sla'>(() => {
    const h = window.location.hash.slice(1);
    return h === 'statuses' || h === 'categories' || h === 'teams' || h === 'sla' ? h : 'areas';
  });
  useEffect(() => {
    window.history.replaceState(null, '', `#${tab}`);
  }, [tab]);
  return (
    <>
      <PageHeader title="Ticket-Konfiguration" description="Bereiche, Kundenportal-Zugriff, Status und Workflow, Kategorien, Teams und SLA-Richtlinien" />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'areas', label: 'Bereiche' },
          { value: 'statuses', label: 'Status & Workflow' },
          { value: 'categories', label: 'Kategorien' },
          { value: 'teams', label: 'Teams' },
          { value: 'sla', label: 'SLA' },
        ]}
      />
      <div className="pt-4">
        {tab === 'areas' && <AreasTab />}
        {tab === 'statuses' && <StatusesTab />}
        {tab === 'categories' && <CategoriesTab />}
        {tab === 'teams' && <TeamsTab />}
        {tab === 'sla' && <SlaTab />}
      </div>
    </>
  );
}
